import api from '@/services/api';
import type {
    DerivedByDimension,
    DerivedScheduleParams,
    DerivedScheduleResponse,
    RawDerivedScheduleResponse,
} from '../types/derivedSchedule.types';

const smrPath = '/smr';

const DERIVED_BY: readonly DerivedByDimension[] = ['time', 'mileage', 'provider'];

// Explicit allow-list, never a defaulting ternary: an unrecognised value still
// needs a total function, so it falls back to 'time'.
function toDerivedBy(raw: string): DerivedByDimension {
    return (DERIVED_BY as readonly string[]).includes(raw) ? (raw as DerivedByDimension) : 'time';
}

function toDueMileage(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function mapResponse(raw: RawDerivedScheduleResponse): DerivedScheduleResponse {
    return {
        resolvedPlan: raw.resolved_plan,
        candidatePlans: raw.candidate_plans ?? [],
        services: (raw.services ?? []).map((entry) => ({
            serviceId: entry.service_id,
            name: entry.name,
            dueDate: entry.due_date,
            intervalMonths: entry.interval_months,
            derivedBy: toDerivedBy(entry.derived_by),
            position: entry.position,
            serviceType: entry.service_type,
            dueMileage: toDueMileage(entry.due_mileage),
        })),
        bounds: raw.bounds,
        startIndex: raw.start_index ?? null,
        startOptions: (raw.start_options ?? []).map((option) => ({
            index: option.index,
            serviceId: option.service_id,
            name: option.name,
            position: option.position,
        })),
    };
}

/**
 * GET smr/products/{product}/derived-schedule (Epic 2.4.2 Slice A). Response
 * mapped snake_case -> camelCase at this boundary per the CLAUDE.md service-layer rule.
 */
export const fetchDerivedSchedule = async (
    productId: string,
    params: DerivedScheduleParams = {}
): Promise<DerivedScheduleResponse> => {
    const queryParams: Record<string, string | number> = {};

    if (params.vehicleAgeYears !== undefined) {
        queryParams.vehicle_age_years = params.vehicleAgeYears;
    }
    if (params.firstRegistrationDate) {
        queryParams.first_registration_date = params.firstRegistrationDate;
    }
    if (params.vehicleMileage !== undefined) {
        queryParams.vehicle_mileage = params.vehicleMileage;
    }
    if (params.annualMileage !== undefined) {
        queryParams.annual_mileage = params.annualMileage;
    }
    if (params.lastServiceDate) {
        queryParams.last_service_date = params.lastServiceDate;
    }
    if (params.lastServiceMileage !== undefined) {
        queryParams.last_service_mileage = params.lastServiceMileage;
    }
    if (params.planId) {
        queryParams.plan_id = params.planId;
    }
    if (params.count !== undefined) {
        queryParams.count = params.count;
    }
    if (params.makeId) {
        queryParams.make_id = params.makeId;
    }
    if (params.modelId) {
        queryParams.model_id = params.modelId;
    }
    if (params.derivativeId) {
        queryParams.derivative_id = params.derivativeId;
    }
    if (params.transmissionId) {
        queryParams.transmission_id = params.transmissionId;
    }
    if (params.fuelTypeId) {
        queryParams.fuel_type_id = params.fuelTypeId;
    }
    if (params.engineSize !== undefined) {
        queryParams.engine_size = params.engineSize;
    }
    if (params.is4x4) {
        queryParams.is_4x4 = 'true';
    }
    if (params.startEntryIndex !== undefined && params.startEntryIndex !== null) {
        queryParams.start_entry_index = params.startEntryIndex;
    }
    if (params.dateOverrides) {
        for (const [row, date] of Object.entries(params.dateOverrides)) {
            queryParams[`date_overrides[${row}]`] = date;
        }
    }
    if (params.cascadeFrom !== undefined) {
        queryParams.cascade_from = params.cascadeFrom;
    }

    const response = await api.get<RawDerivedScheduleResponse>(
        `${smrPath}/products/${productId}/derived-schedule`,
        { params: queryParams }
    );

    return mapResponse(response.data);
};
